import React, { useState, useEffect } from "react";
import { socket } from "../socket";
import { useGameTimer } from "../gameTimer";
import { PlayerAvatar, PlayerTag } from "./PlayerIdentity";
import { t } from "../i18n";
import { useSounds, useCountdownTick } from "../sound";
import { alertDialog } from "../dialog";

// Mode Buzzer — socle (jalon 1) : quiz QCM découpé en manches, cadencé par le
// serveur. Les jokers et les autres formats de manche arrivent aux jalons
// suivants (voir docs/mode-buzzer.md).
function BuzzerQuiz({ gameId, player }) {
  const [state, setState]     = useState(null);   // dernier état reçu du serveur
  const [myChoice, setChoice] = useState(null);
  const [reveal, setReveal]   = useState(null);   // fin de question
  const [brk, setBrk]         = useState(null);   // pause entre deux manches
  const [now, setNow]         = useState(Date.now());
  // Jokers
  const [jokerWin, setJokerWin] = useState(null);   // fenêtre de choix ouverte
  const [myJoker, setMyJoker]   = useState(null);   // { joker, target }
  const [aiming, setAiming]     = useState(null);   // joker en attente de cible
  const [stock, setStock]       = useState({});
  const [alert, setAlert]       = useState(null);
  const [blocked, setBlocked]   = useState(null);
  const [poll, setPoll]         = useState(null);
  const [notice, setNotice]     = useState("");
  const [announce, setAnnounce] = useState(null);   // manche ou intermède à venir
  // Manches : désignation (partage / vol), choix du thème, révélation par lettres
  const [pickWin, setPickWin]   = useState(null);
  const [pickDone, setPickDone] = useState(null);
  const [themeWin, setThemeWin] = useState(null);
  const [themeMsg, setThemeMsg] = useState(null);
  const [myRank, setMyRank]     = useState(null);
  // Intermèdes : loto quiz et course au joker
  const [loto, setLoto]         = useState(null);   // tour en cours
  const [lotoRes, setLotoRes]   = useState(null);   // carte retournée
  const [lotoEnd, setLotoEnd]   = useState(null);
  const [course, setCourse]     = useState(null);   // { item, correct, target }
  const [courseFb, setCourseFb] = useState(null);   // retour sur la dernière réponse
  const [courseEnd, setCourseEnd] = useState(null);
  // Jokers de triche : écran de préparation, coup d'œil, seconde chance, vol de temps
  const [prep, setPrep]         = useState(null);   // { offers, chosen, done, remaining, at }
  const [peek, setPeek]         = useState(null);   // question lue en avance
  const [cheatPick, setCheatPick] = useState(null); // joker de triche retenu (jokers au choix)
  const [secondWrong, setSecondWrong] = useState(null);
  const [tooLate, setTooLate]   = useState(false);

  const myId = player?.playerId;
  const { playPop, playSuccess, playFail } = useSounds();

  // Libellé et description d'un joker, depuis le dictionnaire
  const jLabel = (id) => t(`buzzer.j${id.charAt(0).toUpperCase()}${id.slice(1)}`);
  // Secondes restantes, bornées à la durée annoncée. Sans ce plafond, un
  // `now` en retard — onglet en arrière-plan, téléphone verrouillé, où le
  // navigateur ralentit les minuteurs — affiche un compte supérieur à la durée
  // de la phase, ce qui n'a aucun sens à l'écran.
  const secsLeft = (duration, startedAt) => {
    if (!duration || !startedAt) return 0;
    const raw = Math.ceil((duration - (now - startedAt)) / 1000);
    return Math.max(0, Math.min(Math.ceil(duration / 1000), raw));
  };
  const kindLabel = (k) => t(`buzzer.kind${(k || "classique").charAt(0).toUpperCase()}${(k || "classique").slice(1)}`);
  const jDesc  = (id) => t(`buzzer.j${id.charAt(0).toUpperCase()}${id.slice(1)}Desc`);
  const CHEAT_JOKERS = ["peek", "time", "second", "silence"];

  useEffect(() => {
    const applyState = (s) => {
      setState(s); setChoice(s.myChoice); setReveal(null); setBrk(null); setJokerWin(null);
      setPickWin(null); setPickDone(null); setThemeWin(null); setMyRank(null);
      setLoto(null); setLotoRes(null); setLotoEnd(null); setCourse(null); setCourseEnd(null);
      if (s.myJoker !== undefined) setMyJoker(s.myJoker);
      if (s.stock) setStock(s.stock);
      setPrep(null); setPeek(null); setTooLate(false);
      setSecondWrong(s.secondWrong ?? null);
    };
    socket.on("bzState", applyState);
    socket.on("bzQuestion", applyState);
    socket.on("bzAnswerAck", ({ choice, rank }) => { setChoice(choice); setMyRank(rank ?? null); });
    socket.on("bzPlayers", ({ players }) => setState(s => s ? { ...s, players } : s));
    socket.on("bzRoundEnd", (r) => {
      const mine = r.results?.find(x => x.playerId === myId);
      if (mine?.correct) playSuccess();
      else if (mine?.choice != null) playFail();
      setReveal(r);
      setState(s => s ? { ...s, players: r.players, startedAt: null } : s);
    });
    socket.on("bzRoundBreak", (b) => {
      setBrk(b); setReveal(null);
      setState(s => s ? { ...s, players: b.players, startedAt: null } : s);
    });
    socket.on("bzPrep", (p) => { setPrep({ ...p, at: Date.now() }); setAnnounce(null); });
    socket.on("bzPrepEnd", () => setPrep(null));
    socket.on("bzPeek", ({ question }) => setPeek(question));
    socket.on("bzSecondChance", ({ wrong }) => { playFail(); setSecondWrong(wrong); setChoice(null); });
    socket.on("bzTooLate", () => setTooLate(true));
    socket.on("bzJokerWindow", (w) => {
      playPop();
      setJokerWin(w); setStock(w.stock || {}); setAnnounce(null); setPeek(null); setPrep(null);
      setMyJoker(null); setAiming(null); setBlocked(null); setPoll(null);
      setAlert(null); setNotice(""); setReveal(null); setBrk(null);
    });
    socket.on("bzJokerResult", (r) => {
      if (r.ok) { setMyJoker({ joker: r.joker, target: r.target }); setStock(r.stock || {}); setAiming(null); }
      else { setNotice(r.error || ""); setTimeout(() => setNotice(""), 2500); }
    });
    // Un vol se prépare : toute la table est prévenue, mais personne ne sait
    // qui vise qui. À chacun de décider s'il se croit visé.
    socket.on("bzAlert", (a) => setAlert(a));
    socket.on("bzAnnounce", (a) => {
      // On horodate à la réception : le compte à rebours n'a pas à dépendre de
      // l'horloge du serveur.
      setAnnounce({ ...a, at: Date.now() }); setReveal(null); setBrk(null); setJokerWin(null);
      setLoto(null); setLotoEnd(null); setCourse(null); setCourseEnd(null);
    });
    socket.on("bzBlocked", (b) => setBlocked(b));
    socket.on("bzPoll", ({ tally }) => setPoll(tally));
    socket.on("bzLetters", ({ answers }) =>
      setState(s => s ? { ...s, question: { ...s.question, answers } } : s));
    socket.on("bzPickWindow", (w) => { setPickWin(w); setPickDone(null); });
    socket.on("bzPickResult", (r) => {
      setPickDone(r); setPickWin(null);
      setState(s => s ? { ...s, players: r.players } : s);
    });
    socket.on("bzThemeWindow", (w) => { setThemeWin(w); setThemeMsg(null); });
    socket.on("bzThemeChosen", (c) => { setThemeWin(null); setThemeMsg(c); });
    socket.on("bzLotoTurn", (tn) => { setLoto(tn); setLotoRes(null); setLotoEnd(null); setBrk(null); });
    socket.on("bzLotoResult", (r) => setLotoRes(r));
    socket.on("bzLotoEnd", (e) => { setLotoEnd(e); setLoto(null); setLotoRes(null); });
    socket.on("bzCourseStart", (st) => {
      setCourse({ item: st.item, correct: 0, target: st.target, startedAt: st.startedAt, duration: st.duration });
      setCourseFb(null); setCourseEnd(null); setBrk(null); setLotoEnd(null);
    });
    socket.on("bzCourseNext", (nx) => {
      setCourse(c => c ? { ...c, item: nx.item, correct: nx.correct } : c);
      setCourseFb(nx.wasRight);
    });
    socket.on("bzCourseDone", (d) => setCourse(c => c ? { ...c, done: d.rank } : c));
    socket.on("bzCourseEnd", (e) => { setCourseEnd(e); setCourse(null); });
    socket.on("bzError", ({ error }) => alertDialog(error));

    socket.emit("bzGetState", { gameId });
    return () => {
      ["bzState", "bzQuestion", "bzAnswerAck", "bzPlayers", "bzRoundEnd", "bzRoundBreak", "bzError",
       "bzJokerWindow", "bzJokerResult", "bzAlert", "bzBlocked", "bzPoll",
       "bzLetters", "bzPickWindow", "bzPickResult", "bzThemeWindow", "bzThemeChosen",
       "bzLotoTurn", "bzLotoResult", "bzLotoEnd", "bzCourseStart", "bzCourseNext", "bzCourseDone", "bzCourseEnd",
       "bzAlert", "bzAnnounce", "bzPrep", "bzPrepEnd", "bzPeek", "bzSecondChance", "bzTooLate"]
        .forEach(e => socket.off(e));
    };
  }, [gameId, myId, playPop, playSuccess, playFail]);

  // L'horloge tourne en permanence : la conditionner à une question en cours
  // figeait le compte à rebours de la fenêtre de joker, de l'annonce et des
  // intermèdes, qui n'ont pas de `startedAt`.
  useEffect(() => {
    const iv = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(iv);
  }, []);

  // Le tic-tac suit la question en cours ; il faut le déclarer ici, avant les
  // rendus des intermèdes qui sortent du composant en avance.
  const questionLeft = state?.startedAt && !reveal ? secsLeft(state.duration, state.startedAt) : null;
  useCountdownTick(questionLeft, { active: questionLeft !== null });
  // Le décompte rejoint la barre de navigation ; pendant la révélation, le rond
  // reste allumé mais sans chiffre.
  useGameTimer(
    questionLeft !== null ? { remaining: questionLeft, duration: state?.duration }
    : state?.startedAt && reveal ? { remaining: 0, duration: state?.duration, idle: true }
    : null
  );

  const pick = (i) => {
    if (myChoice !== null || !state?.startedAt || reveal || tooLate || i === secondWrong) return;
    socket.emit("bzAnswer", { gameId, index: state.index, choice: i });
  };

  // Libellé d'une carte de loto
  const cardLabel = (card) => {
    if (!card) return "";
    if (card.kind === "points") return t("buzzer.cardPoints", { amount: card.amount > 0 ? `+${card.amount}` : card.amount });
    if (card.kind === "joker") return t("buzzer.cardJoker", { joker: jLabel(card.joker) });
    return t("buzzer.cardNothing");
  };

  // ── Préparation : chacun choisit son joker de triche ───────────────────────
  if (prep?.pick) {
    // Jokers au choix : chacun compose sa main, et ajoute son joker de triche
    const left = secsLeft(prep.remaining, prep.at);
    const offers = prep.offers || [];
    const { jokers, catalogue, exclusive, count } = prep.pick;
    const toggle = (id) => socket.emit("bzPrepJokers", {
      gameId, jokers: jokers.includes(id) ? jokers.filter(x => x !== id) : [...jokers, id],
    });
    return (
      <div className="BuzzerQuiz">
        <div className="bz-jokerwin bz-prep">
          <span className="bz-jokerwin-theme">{t("buzzer.prepPickTitle")}</span>
          <span className="bz-announce-count">{left}</span>
          {prep.done ? (
            <>
              <h3 className="bz-jokerwin-title">{t("buzzer.prepReady")}</h3>
              <p className="bz-joker-chosen">
                {jokers.map(jLabel).concat(prep.chosen ? `${jLabel(prep.chosen.joker)} ×${prep.chosen.count}` : []).join(" · ")}
              </p>
            </>
          ) : (
            <>
              <p className={`bz-joker-hint${jokers.length >= count ? " full" : ""}`}>
                {t("buzzer.prepPickHint", { n: jokers.length, max: count })}
              </p>
              <div className="bz-joker-grid bz-prep-grid">
                {catalogue.map(id => {
                  const on = jokers.includes(id);
                  const clash = (exclusive.find(pair => pair.includes(id)) || []).some(o => o !== id && jokers.includes(o));
                  return (
                    <button key={id} className={`bz-joker-card${on ? " active" : ""}`}
                      disabled={!on && (clash || jokers.length >= count)} onClick={() => toggle(id)}>
                      <span className="bz-joker-name">{jLabel(id)}</span>
                      <span className="bz-joker-desc">{jDesc(id)}</span>
                    </button>
                  );
                })}
              </div>
              {!!offers.length && (
                <>
                  <p className="bz-joker-hint">{t("buzzer.prepCheatHint", { max: prep.max })}</p>
                  <div className="bz-joker-grid bz-prep-grid">
                    {offers.map(o => (
                      <button key={o.itemId} className={`bz-joker-card cheat${cheatPick === o.itemId ? " active" : ""}`}
                        onClick={() => setCheatPick(cheatPick === o.itemId ? null : o.itemId)}>
                        <span className="bz-joker-name">{jLabel(o.joker)}</span>
                        <span className="bz-joker-desc">{jDesc(o.joker)}</span>
                        <span className="bz-joker-count">{t("buzzer.prepStock", { n: o.qty })}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}
              <button className="btn btn-primary"
                onClick={() => socket.emit("bzPrepChoose", { gameId, itemId: cheatPick, jokers })}>
                {t("buzzer.prepValidate")}
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  if (prep) {
    const left = secsLeft(prep.remaining, prep.at);
    const offers = prep.offers || [];
    return (
      <div className="BuzzerQuiz">
        <div className="bz-jokerwin">
          <span className="bz-jokerwin-theme">{t("buzzer.prepTitle")}</span>
          <span className="bz-announce-count">{left}</span>
          {!offers.length ? (
            <h3 className="bz-jokerwin-title">{t("buzzer.prepWaiting")}</h3>
          ) : prep.done ? (
            <h3 className="bz-jokerwin-title">
              {prep.chosen
                ? t("buzzer.prepChosen", { joker: jLabel(prep.chosen.joker), n: prep.chosen.count })
                : t("buzzer.prepSkipped")}
            </h3>
          ) : (
            <>
              <p className="bz-joker-hint">{t("buzzer.prepHint", { max: prep.max })}</p>
              <div className="bz-joker-grid">
                {offers.map(o => (
                  <button key={o.itemId} className="bz-joker-card cheat"
                    onClick={() => socket.emit("bzPrepChoose", { gameId, itemId: o.itemId })}>
                    <span className="bz-joker-name">{jLabel(o.joker)}</span>
                    <span className="bz-joker-desc">{jDesc(o.joker)}</span>
                    <span className="bz-joker-count">{t("buzzer.prepStock", { n: o.qty })}</span>
                  </button>
                ))}
              </div>
              <button className="btn bz-joker-cancel" onClick={() => socket.emit("bzPrepChoose", { gameId, itemId: null })}>
                {t("buzzer.prepSkip")}
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  // ── Intermède : loto quiz ─────────────────────────────────────────────────
  if (loto || lotoRes) {
    const turn = loto || {};
    const mine = turn.playerId === myId;
    const board = lotoRes?.board || [];
    return (
      <div className="BuzzerQuiz">
        <div className="bz-jokerwin">
          <span className="bz-jokerwin-theme">{t("buzzer.lotoTitle")}</span>
          <h3 className="bz-jokerwin-title">
            {lotoRes
              ? t("buzzer.lotoGot", { pseudo: lotoRes.pseudo, what: cardLabel(lotoRes.card) })
              : mine ? t("buzzer.lotoYourTurn") : t("buzzer.lotoWaiting", { pseudo: turn.pseudo })}
          </h3>
          {!lotoRes && <span className="bz-joker-hint">{t("buzzer.lotoHint")}</span>}
          <div className="bz-loto-board">
            {[0, 1, 2, 3].map(i => {
              const revealed = lotoRes && board[i];
              const isPicked = lotoRes?.index === i;
              return (
                <button key={i}
                  className={`bz-loto-card${revealed ? " revealed" : ""}${isPicked ? " picked" : ""}`}
                  disabled={!mine || !!lotoRes}
                  onClick={() => socket.emit("bzLotoPick", { gameId, index: i })}>
                  {revealed ? <span className="bz-loto-face">{cardLabel(board[i])}</span> : <span className="bz-loto-back">?</span>}
                </button>
              );
            })}
          </div>
          {turn.turn !== undefined && !lotoRes && (
            <span className="bz-joker-hint">{t("buzzer.lotoTurnOf", { turn: turn.turn + 1, total: turn.total })}</span>
          )}
        </div>
      </div>
    );
  }

  // ── Intermède : course au joker ───────────────────────────────────────────
  if (course) {
    return (
      <div className="BuzzerQuiz">
        <div className="bz-jokerwin">
          <span className="bz-jokerwin-theme">{t("buzzer.courseTitle")}</span>
          {course.done ? (
            <h3 className="bz-jokerwin-title">
              {course.done === 1 ? t("buzzer.courseDoneFirst") : t("buzzer.courseDone", { rank: course.done })}
            </h3>
          ) : (
            <>
              <span className="bz-joker-hint">{t("buzzer.courseHint", { n: course.target })}</span>
              <div className="bz-course-progress">
                {t("buzzer.courseProgress", { correct: course.correct, target: course.target })}
              </div>
              {courseFb !== null && (
                <p className={courseFb ? "bz-reveal-good" : "bz-reveal-bad"}>
                  {t(courseFb ? "buzzer.courseRight" : "buzzer.courseWrong")}
                </p>
              )}
              <h3 className="bz-course-question">{course.item?.question}</h3>
              <p className="bz-course-claim">« {course.item?.claim} »</p>
              <div className="bz-course-buttons">
                <button className="bz-course-btn yes" onClick={() => socket.emit("bzCourseAnswer", { gameId, value: true })}>
                  {t("buzzer.courseTrue")}
                </button>
                <button className="bz-course-btn no" onClick={() => socket.emit("bzCourseAnswer", { gameId, value: false })}>
                  {t("buzzer.courseFalse")}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // Résultats d'un intermède, avant de reprendre les manches
  if (courseEnd || lotoEnd) {
    const awards = courseEnd?.awards || [];
    return (
      <div className="BuzzerQuiz">
        <div className="bz-break">
          <span className="bz-break-label">
            {courseEnd ? t("buzzer.courseResults") : t("buzzer.lotoTitle")}
          </span>
          <ul className="bz-break-scores">
            {(courseEnd ? awards : (lotoEnd?.results || [])).map((r, i) => (
              <li key={r.playerId}>
                <span className="bz-rank-pos">{i + 1}</span>
                <span className="bz-player-name">{r.pseudo}</span>
                <b>{courseEnd ? t("buzzer.courseAward", { pseudo: "", count: r.count }).trim() : cardLabel(r.card)}</b>
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  }

  // ── Manche « thème choisi » : le dernier du classement décide ─────────────
  if (themeWin) {
    const mine = themeWin.chooserId === myId;
    return (
      <div className="BuzzerQuiz">
        <div className="bz-jokerwin">
          <h3 className="bz-jokerwin-title">
            {mine ? t("buzzer.themeTitle") : t("buzzer.themeWaiting", { pseudo: themeWin.chooserPseudo })}
          </h3>
          {mine && (
            <div className="bz-joker-grid">
              {themeWin.themes.map(th => (
                <button key={th} className="bz-joker-card"
                  onClick={() => socket.emit("bzPickTheme", { gameId, theme: th })}>
                  <span className="bz-joker-name">{th}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Désignation après une manche Partage ou Vol ───────────────────────────
  if (pickWin) {
    const mine = pickWin.winnerId === myId;
    const others = (pickWin.players || []).filter(p => p.playerId !== myId);
    return (
      <div className="BuzzerQuiz">
        <div className="bz-jokerwin">
          <h3 className="bz-jokerwin-title">
            {mine
              ? t(pickWin.kind === "share" ? "buzzer.shareTitle" : "buzzer.stealTitle")
              : t("buzzer.pickWaiting", { pseudo: pickWin.winnerPseudo })}
          </h3>
          {mine && (
            <>
              <span className="bz-jokerwin-theme">
                {t(pickWin.kind === "share" ? "buzzer.shareHint" : "buzzer.stealHint", { amount: pickWin.amount })}
              </span>
              <div className="bz-joker-targets">
                {others.map(p => (
                  <button key={p.playerId} className="bz-joker-target"
                    onClick={() => socket.emit("bzPick", { gameId, targetId: p.playerId })}>
                    <PlayerAvatar player={p} size={36} />
                    <span>{p.pseudo}</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // ── Annonce : ce qui arrive, ce qu'il faut faire, et le temps de le lire ──
  if (announce) {
    const left = secsLeft(announce.duration, announce.at);
    const isRound = announce.kind === "round";
    return (
      <div className="BuzzerQuiz">
        <div className="bz-announce">
          <span className="bz-announce-kicker">
            {isRound
              ? t("buzzer.announceRound", { round: announce.round, total: announce.roundTotal })
              : t("buzzer.announceInterlude")}
          </span>
          <h2 className="bz-announce-title">
            {isRound ? kindLabel(announce.id) : t(`buzzer.inter.${announce.id}`)}
          </h2>
          <p className="bz-announce-text">{t(`buzzer.howto.${announce.id}`)}</p>
          <span className="bz-announce-count">{left}</span>
        </div>
      </div>
    );
  }

  // ── Fenêtre de choix : seul le thème est connu ────────────────────────────
  if (jokerWin) {
    const jw = jokerWin;
    const left = secsLeft(jw.duration, jw.startedAt);
    const others = (jw.players || []).filter(p => p.playerId !== myId);
    return (
      <div className="BuzzerQuiz">
        <div className="bz-jokerwin">
          <div className="bz-timer small">
            <svg viewBox="0 0 40 40">
              <circle className="bz-timer-bg" r="18" cx="20" cy="20" />
              <circle className="bz-timer-fg" r="18" cx="20" cy="20"
                style={{ strokeDashoffset: 113 * Math.min(1, (now - jw.startedAt) / jw.duration) }} />
              <text x="20" y="20">{left}</text>
            </svg>
          </div>
          <h3 className="bz-jokerwin-title">{t("buzzer.jokerTitle")}</h3>
          <span className="bz-jokerwin-theme">{t("buzzer.jokerTheme", { theme: jw.theme })}</span>
          {alert && <p className="bz-steal-alert">{t("buzzer.alertSteal")}</p>}

          {myJoker ? (
            <p className="bz-joker-chosen">
              {t("buzzer.jokerChosen", { joker: jLabel(myJoker.joker) })}
              {myJoker.target && ` → ${(jw.players.find(p => p.playerId === myJoker.target) || {}).pseudo || ""}`}
            </p>
          ) : aiming ? (
            <>
              <p className="bz-joker-aim">{t("buzzer.jokerPickTarget")}</p>
              <div className="bz-joker-targets">
                {others.map(p => (
                  <button key={p.playerId} className="bz-joker-target"
                    onClick={() => socket.emit("bzUseJoker", { gameId, index: jw.index, joker: aiming, target: p.playerId })}>
                    <PlayerAvatar player={p} size={36} />
                    <span>{p.pseudo}</span>
                  </button>
                ))}
              </div>
              <button className="btn bz-joker-cancel" onClick={() => setAiming(null)}>{t("buzzer.jokerCancel")}</button>
            </>
          ) : (
            <div className="bz-joker-grid">
              {(jw.enabled || []).map(id => {
                const n = stock[id] ?? 0;
                const targeted = ["steal", "block"].includes(id);
                return (
                  <button key={id} className={`bz-joker-card${CHEAT_JOKERS.includes(id) ? " cheat" : ""}`} disabled={n <= 0}
                    onClick={() => targeted
                      ? setAiming(id)
                      : socket.emit("bzUseJoker", { gameId, index: jw.index, joker: id })}>
                    <span className="bz-joker-name">
                      {jLabel(id)}
                      {CHEAT_JOKERS.includes(id) && <em className="bz-cheat-tag">{t("buzzer.cheatTag")}</em>}
                    </span>
                    <span className="bz-joker-desc">{jDesc(id)}</span>
                    <span className={`bz-joker-count${n <= 0 ? " empty" : ""}`}>
                      {n > 0 ? t("buzzer.jokerLeft", { n }) : t("buzzer.jokerEmpty")}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
          {/* Coup d'œil : la question, lisible mais pas encore jouable */}
          {peek && (
            <div className="bz-peek">
              <span className="bz-peek-title">{t("buzzer.peekTitle")}</span>
              <p className="bz-peek-question">{peek.question}</p>
              {!!(peek.answers || []).length && (
                <ol className="bz-peek-answers">
                  {peek.answers.map((a, i) => <li key={i}><b>{"ABCD"[i]}</b> {a}</li>)}
                </ol>
              )}
            </div>
          )}
          {notice && <p className="bz-notice">{notice}</p>}
        </div>
      </div>
    );
  }

  if (!state?.question) {
    return <div className="BuzzerQuiz"><p className="bz-waiting">{t("buzzer.waiting")}</p></div>;
  }

  const { question, players = [], startedAt } = state;
  const myResult  = reveal?.results.find(r => r.playerId === myId);
  const ranked    = [...players].sort((a, b) => (b.score || 0) - (a.score || 0));

  // Pause entre deux manches : on affiche le classement du moment
  if (brk) {
    return (
      <div className="BuzzerQuiz">
        <div className="bz-break">
          <span className="bz-break-label">{t("buzzer.roundBreak", { round: brk.finishedRound + 1 })}</span>
          {brk.nextKind && (
            <h3 className="bz-break-next">
              {t("buzzer.nextRound", { round: brk.nextRound + 1, kind: kindLabel(brk.nextKind) })}
            </h3>
          )}
          <ul className="bz-break-scores">
            {ranked.map((p, i) => (
              <li key={p.playerId}>
                <PlayerTag player={p} pos={i + 1} score={p.score}
                  className={p.playerId === myId ? "me" : ""} />
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  }

  return (
    <div className="BuzzerQuiz">
      <div className="bz-header">
        <div className="bz-header-info">
          <span className="bz-round-badge">
            {t("buzzer.roundBadge", {
              round: state.round + 1, total: state.roundTotal,
              q: state.roundQuestion, qTotal: state.roundSize,
            })}
          </span>
          {/* La pastille dit déjà la manche et la question : ici, seulement
              le type d'une manche spéciale. */}
          {state.roundKind && state.roundKind !== "classique" && (
            <p className="bz-progress"><span className="bz-kind-tag">{kindLabel(state.roundKind)}</span></p>
          )}
        </div>
      </div>

      <div className="bz-layout">
        <main className="bz-main">
          {question.media_url && (
            /\.(jpe?g|png|gif|webp)$/i.test(question.media_url)
              ? <img src={question.media_url} alt="" className="quiz-media" />
              : <audio controls src={question.media_url} />
          )}
          <h3 className="bz-question">{question.question}</h3>

          {/* On n'affiche un joker que s'il me concerne : être bloqué, être
              volé. Le reste ne regarde que son porteur. */}
          {blocked && <p className="bz-blocked">{t("buzzer.blockedBy", { pseudo: blocked.byPseudo })}</p>}
          {state.timeStolen && !reveal && !tooLate && <p className="bz-blocked">{t("buzzer.timeStolen")}</p>}
          {tooLate && !reveal && <p className="bz-blocked">{t("buzzer.tooLate")}</p>}
          {secondWrong !== null && myChoice === null && !reveal && <p className="bz-notice">{t("buzzer.secondChance")}</p>}

          <div className="bz-choices">
            {(question.answers || []).map((a, i) => {
              const isCorrect = reveal && i === reveal.correctAnswer;
              const isMyWrong = reveal && myChoice === i && i !== reveal.correctAnswer;
              // 50/50 : le serveur a désigné les 2 mauvaises réponses à masquer
              const hidden = (state.hidden || []).includes(i) && !reveal;
              // Seconde chance : la première réponse, fausse, reste barrée
              const firstTry = secondWrong === i && !reveal;
              return (
                <button key={i}
                  className={`bz-choice${myChoice === i ? " picked" : ""}${isCorrect ? " correct" : ""}${isMyWrong ? " wrong" : ""}${hidden ? " hidden" : ""}${firstTry ? " wrong" : ""}`}
                  disabled={hidden || firstTry || myChoice !== null || !startedAt || !!reveal}
                  onClick={() => pick(i)}>
                  <span className="bz-choice-key">{"ABCD"[i]}</span>
                  <span className="bz-choice-text">{hidden ? "—" : a}</span>
                  {poll && !reveal && poll[i] > 0 && <span className="bz-poll-count">{poll[i]}</span>}
                </button>
              );
            })}
          </div>

          {reveal ? (
            <div className="bz-reveal">
              <p className="bz-reveal-answer">
                {t("buzzer.correctAnswer", { answer: reveal.correctText })}
              </p>
              {/* Ce que les jokers m'ont fait, ou fait faire */}
              {(reveal.steals || []).map((st, i) => {
                if (st.target === myId && st.blockedBy === "shield") return <p key={i} className="bz-reveal-good">{t("buzzer.shieldSaved")}</p>;
                if (st.by === myId && st.mirrored) return <p key={i} className="bz-reveal-good">{t("buzzer.mirrorBack")}</p>;
                const name = (pid) => players.find(p => p.playerId === pid)?.pseudo || "?";
                if (st.target === myId && st.amount > 0) return <p key={i} className="bz-reveal-bad">{t("buzzer.stolen", { thief: name(st.by), points: st.amount })}</p>;
                if (st.by === myId && st.amount > 0) return <p key={i} className="bz-reveal-good">{t("buzzer.stoleFrom", { victim: name(st.target), points: st.amount })}</p>;
                return null;
              })}
              {(reveal.silenced || []).some(x => x.playerId === myId) && (
                <p className="bz-reveal-bad">{t("buzzer.silencedMine")}</p>
              )}
              {(reveal.silencers || []).includes(myId) && (reveal.silenced || []).length > 0 && (
                <p className="bz-reveal-good">{t("buzzer.silenceDone", { n: reveal.silenced.length })}</p>
              )}
              {myResult && (
                <p className={myResult.correct ? "bz-reveal-good" : "bz-reveal-bad"}>
                  {myResult.correct
                    ? t("buzzer.youGood", { points: myResult.delta })
                    : t("buzzer.youBad")}
                </p>
              )}
            </div>
          ) : myChoice !== null ? (
            <p className="bz-waiting">
              {/* En rapidité et en éclair, la place dans l'ordre décide des points */}
              {myRank && ["rapidite", "eclair"].includes(state.roundKind)
                ? t(myRank <= 3 ? `buzzer.rank${myRank}` : "buzzer.rankN", { n: myRank })
                : t("buzzer.waitingOthers")}
            </p>
          ) : null}
          {themeMsg && <p className="bz-theme-msg">{t("buzzer.themeChosen", { theme: themeMsg.theme })}</p>}
          {pickDone && (
            <p className="bz-pick-done">
              {t(pickDone.kind === "share" ? "buzzer.shareDone" : "buzzer.stealDone", {
                winner: pickDone.winnerPseudo, target: pickDone.targetPseudo, amount: pickDone.amount,
              })}
            </p>
          )}
        </main>

        {/* Après la révélation la liste passe en grand : c'est le moment où
            chacun regarde qui a marqué. */}
        <aside className={`bz-players${reveal ? " big" : ""}`}>
          {ranked.map((p, i) => {
            const res = reveal?.results?.find(r => r.playerId === p.playerId);
            return (
              <PlayerTag key={p.playerId} player={p} pos={i + 1} score={p.score}
                avatarSize={reveal ? 40 : 30}
                className={`bz-player${p.playerId === myId ? " me" : ""}${p.offline ? " offline" : ""}`
                  + (res ? (res.correct ? " good" : " bad") : "")}
                afterName={p.answered && !reveal ? <span className="bz-answered">✓</span> : null}
                /* Le gain reste affiché à côté du total : tout le monde voit
                   les points monter, pas seulement le sien. */
                beforeScore={res && res.delta !== 0 ? (
                  <span className={`bz-delta${res.delta > 0 ? " up" : " down"}`}>
                    {res.delta > 0 ? `+${res.delta}` : res.delta}
                  </span>
                ) : null} />
            );
          })}
        </aside>
      </div>
    </div>
  );
}

export default BuzzerQuiz;
